import { useState } from "react";
import { useBrand, logoUrl, loginBgUrl, BrandText } from "@/lib/brand";
import { InstallApp } from "@/components/InstallApp";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Megaphone } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [username, setU] = useState("");
  const [password, setP] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const brand = useBrand();
  const logo = logoUrl(brand);
  const bg = loginBgUrl(brand);
  const hasBrand = !!(logo || brand.company_name || brand.title || brand.subtitle);

  if (user) {
    const to = loc.state?.from?.pathname || "/";
    nav(to, { replace: true });
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(username.trim(), password);
    setLoading(false);
    if (!res.ok) { setError(res.error); return; }
    toast.success("Hoş geldiniz!");
    nav("/", { replace: true });
  };

  return (
    <div className={`${bg ? "force-dark " : ""}min-h-screen grain bg-slate-950 flex items-center justify-center px-4 py-10 relative overflow-hidden`}>
      {bg ? (
        <div className="absolute inset-0 pointer-events-none" data-testid="login-bg">
          <img src={bg} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-black" style={{ opacity: (brand.bg_dim ?? 55) / 100 }} />
        </div>
      ) : (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        </div>
      )}

      <div className={`relative z-10 w-full grid gap-8 items-center ${hasBrand ? "max-w-5xl lg:grid-cols-5" : "max-w-md"}`}>
        {hasBrand && (
          <div className="lg:col-span-2 space-y-5 animate-fade-up" data-testid="login-brand">
            {logo && <img src={logo} alt="logo" className="h-10 sm:h-16 w-auto max-w-[280px] object-contain" data-testid="login-logo" />}
            {brand.company_name && <div className="text-lg text-white" data-testid="login-company"><BrandText text={brand.company_name} testId="login-company-text" /></div>}
            {brand.title && <h1 className="font-display text-4xl sm:text-5xl font-bold leading-[1.05] tracking-tight whitespace-pre-line" data-testid="login-title">{brand.title}</h1>}
            {brand.subtitle && <p className="text-slate-400 text-base max-w-md whitespace-pre-line" data-testid="login-subtitle">{brand.subtitle}</p>}
          </div>
        )}

        <Card className={`${hasBrand ? "lg:col-span-3" : ""} border-slate-800 bg-slate-900/60 glass animate-fade-up delay-100`}>
          <CardContent className="p-8 sm:p-10">
            {brand.daily_message && (
              <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-3 animate-fade-up" data-testid="login-daily-message">
                <Megaphone size={18} className="text-amber-300 shrink-0 mt-0.5" />
                <div><div className="text-[10px] uppercase font-mono tracking-widest text-amber-300/80">Günün Mesajı</div>
                  <div className="text-sm text-amber-50 leading-snug whitespace-pre-line">{brand.daily_message}</div></div>
              </div>
            )}
            <h2 className="font-display text-2xl font-bold mb-6">Giriş Yap</h2>

            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="u" className="text-slate-300">Kullanıcı Adı</Label>
                <Input
                  id="u"
                  data-testid="login-username"
                  value={username}
                  onChange={(e) => setU(e.target.value)}
                  className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-100"
                  placeholder="kullanıcı adınız"
                  autoComplete="username"
                />
              </div>
              <div>
                <Label htmlFor="p" className="text-slate-300">Şifre</Label>
                <Input
                  id="p"
                  data-testid="login-password"
                  type="password"
                  inputMode="numeric"
                  value={password}
                  onChange={(e) => setP(e.target.value)}
                  className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-100"
                  placeholder="6 haneli PIN"
                  autoComplete="current-password"
                />
              </div>

              {error && (
                <Alert variant="destructive" data-testid="login-error" className="bg-red-500/10 border-red-500/40">
                  <AlertDescription className="text-red-300">{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                data-testid="login-submit"
                disabled={loading || !username || !password}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-6 rounded-xl shadow-[0_8px_32px_rgba(37,99,235,0.4)]"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : "Giriş Yap"}
              </Button>
            </form>
          </CardContent>
          <div className="px-6 pb-6 sm:px-8 sm:pb-8 -mt-2"><InstallApp /></div>
        </Card>
      </div>
    </div>
  );
}
